import React, { useContext, useEffect, useState } from 'react';
import { Alert, Card, Spinner } from 'react-bootstrap';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToken from 'hooks/useToken';
import AppContext from 'context/Context';
import VacationDetailsModal from './VacationDetailsModal';
import './TeamVacationsCalendar.css';

const TeamVacationsCalendar = () => {
    const {
        config: { isDark }
    } = useContext(AppContext);
    const { token } = useToken();
    const [showModal, setShowModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [vacations, setVacations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadTeamVacations();
    }, [token]);

    /**
     * Obtiene el color según el estado de la vacación
     * Solo maneja: Aprobada (verde) y Pendiente (amarillo)
     */
    const getColorByStatus = (estado) => {
        const estadoUpper = estado?.toUpperCase();
        
        if (estadoUpper === 'APROBADA') {
            return { 
                bg: '#2DCA8C', 
                border: '#28B57D', 
                text: '#ffffff' 
            };
        }
        
        // Por defecto: Pendiente
        return { 
            bg: '#FFC107', 
            border: '#F0B400', 
            text: '#000000' 
        };
    };

    /**
     * Carga las vacaciones del equipo desde la API
     */
    const loadTeamVacations = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch('/api/vacaciones/ObtenerVacacionesEquipo', {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.iResponseType === 1 && data.detalles) {
                const detalles = JSON.parse(data.detalles);
                const vacacionesData = detalles.table2 || detalles.table1 || [];
                
                const eventos = [];
                
                vacacionesData.forEach(vac => {
                    const colors = getColorByStatus(vac.vEstado);
                    const fotoUrl = vac.vFoto 
                        ? `data:image/jpeg;base64,${vac.vFoto}` 
                        : '/images/profiles/default-profile.png';
                    
                    // Verificar si hay fechas específicas en vFechasJson
                    let fechasEspecificas = [];
                    
                    if (vac.vFechasJson && vac.vFechasJson.trim() !== '') {
                        try {
                            fechasEspecificas = JSON.parse(vac.vFechasJson);
                        } catch (e) {
                            console.warn('Error al parsear vFechasJson:', e);
                            fechasEspecificas = [];
                        }
                    }
                    
                    // Props comunes para todos los eventos
                    const commonProps = {
                        idVacacion: vac.idVacacion,
                        idUsuario: vac.idUsuario, // Agregar el idUsuario para cargar la imagen
                        nombreCompleto: vac.vNombreCompleto || vac.vNombre,
                        puesto: vac.vPuesto || 'No especificado',
                        departamento: vac.vDepartamento || 'No especificado',
                        foto: fotoUrl,
                        diasSolicitados: vac.iDiasSolicitados,
                        estado: vac.vEstado,
                        comentarios: vac.vComentarios || '',
                        categoria: vac.vCategoria || 'Vacaciones',
                        fechaInicioOriginal: vac.dFechaInicio,
                        fechaFinOriginal: vac.dFechaFin,
                        vFechasJson: vac.vFechasJson || null  // ✅ CRÍTICO: Pasar vFechasJson
                    };
                    
                    // Si hay fechas específicas, crear un evento por cada día
                    if (fechasEspecificas && fechasEspecificas.length > 0) {
                        fechasEspecificas.forEach(fecha => {
                            eventos.push({
                                id: `${vac.idVacacion}-${fecha}`,
                                title: vac.vNombreCompleto || vac.vNombre,
                                start: fecha,
                                end: fecha, // Evento de un solo día
                                allDay: true,
                                backgroundColor: colors.bg,
                                borderColor: colors.border,
                                textColor: colors.text,
                                extendedProps: {
                                    ...commonProps,
                                    fechaEspecifica: fecha,
                                    totalFechasEspecificas: fechasEspecificas.length
                                }
                            });
                        });
                    } else {
                        // Si no hay fechas específicas, usar el rango completo (comportamiento anterior)
                        const fechaFin = new Date(vac.dFechaFin);
                        fechaFin.setDate(fechaFin.getDate() + 1);
                        
                        eventos.push({
                            id: vac.idVacacion.toString(),
                            title: vac.vNombreCompleto || vac.vNombre,
                            start: vac.dFechaInicio,
                            end: fechaFin.toISOString().split('T')[0],
                            backgroundColor: colors.bg,
                            borderColor: colors.border,
                            textColor: colors.text,
                            extendedProps: commonProps
                        });
                    }
                });

                setVacations(eventos);
            } else {
                setVacations([]);
            }
        } catch (error) {
            console.error('Error al cargar vacaciones del equipo:', error);
            setError('Error al cargar las vacaciones del equipo. Por favor, intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Maneja el clic en un evento del calendario
     */
    const handleEventClick = (clickInfo) => {
        const event = clickInfo.event;
        const { 
            idVacacion,
            idUsuario, // Agregar idUsuario
            nombreCompleto, 
            puesto, 
            foto, 
            departamento, 
            categoria, 
            diasSolicitados, 
            estado, 
            comentarios,
            fechaInicioOriginal,
            fechaFinOriginal,
            fechaEspecifica,
            totalFechasEspecificas,
            vFechasJson  // ✅ CRÍTICO: Recibir vFechasJson
        } = event.extendedProps;

        setSelectedEmployee({
            idVacacion: idVacacion || event.id,
            idUsuario: idUsuario, // Pasar el idUsuario al modal
            name: nombreCompleto,
            position: puesto,
            photo: foto,
            department: departamento,
            categoria: categoria,
            vacationStart: fechaInicioOriginal,
            vacationEnd: fechaFinOriginal,
            daysRequested: diasSolicitados,
            estado: estado,
            comentarios: comentarios,
            fechaEspecifica: fechaEspecifica,
            totalFechasEspecificas: totalFechasEspecificas,
            vFechasJson: vFechasJson  // ✅ CRÍTICO: Pasar vFechasJson al modal
        });
        setShowModal(true);
    };

    /**
     * Renderiza el contenido de cada evento en el calendario
     */
    const renderEventContent = (eventInfo) => (
        <div style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: eventInfo.backgroundColor,
            color: eventInfo.textColor,
            borderRadius: '4px',
            padding: '4px 8px',
            fontWeight: '500',
            minHeight: '26px',
            boxSizing: 'border-box',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease'
        }}>
            <span style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '0.85rem',
                lineHeight: '1.3',
                width: '100%'
            }}>
                {eventInfo.event.title}
            </span>
        </div>
    );

    return (
        <div className="vacation-calendar-wrapper">
            <Card className="mb-3">
                <Card.Header className={isDark ? 'text-white border-dark' : 'bg-light'}>
                    <h5 className="mb-0">
                        <FontAwesomeIcon icon="calendar" className="me-2" />
                        Calendario de Vacaciones del Equipo
                    </h5>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className={`mt-2 ${isDark ? 'text-white-50' : 'text-muted'}`}>
                                Cargando calendario...
                            </p>
                        </div>
                    ) : error ? (
                        <Alert variant="danger">
                            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                            {error}
                        </Alert>
                    ) : (
                        <>
                            {/* Leyenda de estados */}
                            <div className="mb-3 d-flex gap-3 flex-wrap align-items-center">
                                <small className={isDark ? 'text-white-50' : 'text-muted'}>
                                    <strong>Leyenda:</strong>
                                </small>
                                <small className={isDark ? 'text-white-50' : ''}>
                                    <span className="badge me-1" style={{ backgroundColor: '#2DCA8C' }}>■</span>
                                    Aprobadas
                                </small>
                                <small className={isDark ? 'text-white-50' : ''}>
                                    <span className="badge me-1" style={{ backgroundColor: '#FFC107' }}>■</span>
                                    Pendientes
                                </small>
                            </div>

                            {/* Calendario */}
                            <div className={`calendar-container ${isDark ? 'calendar-dark-mode' : ''}`}>
                                <FullCalendar
                                    plugins={[dayGridPlugin, interactionPlugin]}
                                    initialView="dayGridMonth"
                                    headerToolbar={{
                                        left: 'prev,next today',
                                        center: 'title',
                                        right: 'dayGridMonth'
                                    }}
                                    events={vacations}
                                    eventClick={handleEventClick}
                                    locale="es"
                                    height="auto"
                                    buttonText={{
                                        today: 'Hoy',
                                        month: 'Mes'
                                    }}
                                    dayMaxEvents={3}
                                    eventContent={renderEventContent}
                                    eventClassNames="vacation-event"
                                    fixedWeekCount={false}
                                    showNonCurrentDates={false}
                                />
                            </div>
                        </>
                    )}
                </Card.Body>
                <Card.Footer className={isDark ? 'text-white-50 border-dark' : 'bg-light'}>
                    <small className={isDark ? 'text-white-50' : 'text-muted'}>
                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                        {vacations.length > 0
                            ? `Mostrando ${vacations.length} día${vacations.length !== 1 ? 's' : ''} de vacaciones específicos`
                            : 'No hay vacaciones programadas'}
                        {vacations.length > 0 && ' - Haga clic en un día para ver detalles del empleado'}
                    </small>
                </Card.Footer>
            </Card>

            {/* Modal de detalles */}
            <VacationDetailsModal
                show={showModal}
                onHide={() => setShowModal(false)}
                employee={selectedEmployee}
                onSuccess={loadTeamVacations}
                showActions={true}
            />
        </div>
    );
};

export default TeamVacationsCalendar;

